import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuthSync } from '../../hooks/useAuthSync';
import useOrganizationFeatureTag from '../../hooks/useOrganizationFeatureTag';

/**
 * Default fallback component for access denied
 */
const AccessDeniedPage = ({ userRole, requiredRoles }) => (
  <div style={{
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '60vh',
    textAlign: 'center',
    padding: '2rem'
  }}>
    <div style={{
      fontSize: '4rem',
      color: '#e53e3e',
      marginBottom: '1rem'
    }}>
      🚫
    </div>
    <h2 style={{
      color: '#2d3748',
      marginBottom: '1rem'
    }}>
      Acceso Denegado
    </h2>
    <p style={{
      color: '#718096',
      maxWidth: '400px',
      lineHeight: '1.5'
    }}>
      No tienes permisos para acceder a esta sección. Esta funcionalidad está restringida para usuarios con roles de administrador o propietario.
    </p>
    <p style={{
      color: '#a0aec0',
      fontSize: '0.875rem',
      marginTop: '1rem'
    }}>
      Tu rol actual: <strong>{userRole}</strong><br />
      Roles requeridos: <strong>{requiredRoles.join(', ')}</strong>
    </p>
  </div>
);

/**
 * Route wrapper that protects routes based on user roles
 * @param {ReactNode} children - Component to render if user has access
 * @param {Array|String} allowedRoles - Array of allowed roles or single role string
 * @param {String} redirectTo - Path to redirect if access denied (default: '/')
 * @param {ReactNode} fallback - Component to render if access denied (if redirectTo not provided)
 */
const ProtectedRoute = ({ 
  children, 
  allowedRoles = [], 
  redirectTo = null, 
  fallback = null 
}) => {
  const { getAppRole, isAuthenticated } = useAuthSync();
  const userRole = getAppRole();

  // If not authenticated, redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Normalize allowedRoles to array
  const roles = Array.isArray(allowedRoles) ? allowedRoles : [allowedRoles];

  // Check if user's role is in the allowed roles
  if (roles.length > 0 && !roles.includes(userRole)) {
    if (fallback) {
      return fallback;
    }
    if (redirectTo) {
      return <Navigate to={redirectTo} replace />;
    }
    // Default: show access denied page
    return <AccessDeniedPage userRole={userRole} requiredRoles={roles} />;
  }

  return children;
};

/**
 * Pre-configured component for routes that require admin or owner roles
 */
export const AdminOwnerRoute = ({ children, redirectTo = null, fallback = null }) => (
  <ProtectedRoute allowedRoles={['admin', 'owner']} redirectTo={redirectTo} fallback={fallback}>
    {children}
  </ProtectedRoute>
);

/**
 * Pre-configured component for routes that exclude 'user' role
 */
export const NonUserRoute = ({ children, redirectTo = null, fallback = null }) => (
  <ProtectedRoute allowedRoles={['admin', 'owner']} redirectTo={redirectTo} fallback={fallback}>
    {children}
  </ProtectedRoute>
);

/**
 * Route that allows access when the user has a specific permission.
 * Owner and admin always pass. Users with the matching custom role permission also pass.
 */
export const PermissionRoute = ({ children, permission, redirectTo = null, fallback = null }) => {
  const { getAppRole, hasPermission, isAuthenticated } = useAuthSync();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (hasPermission(permission)) {
    return children;
  }

  if (fallback) return fallback;
  if (redirectTo) return <Navigate to={redirectTo} replace />;

  const userRole = getAppRole();
  return <AccessDeniedPage userRole={userRole} requiredRoles={[permission]} />;
};

/**
 * Route for clinical pages that do not depend on a user permission (e.g. catalogs of
 * fármacos or diagnósticos). Redirects when the organization has medical data disabled.
 */
export const MedicalDataRoute = ({ children, redirectTo = '/' }) => {
  const { isAuthenticated, isMedicalDataEnabled } = useAuthSync();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!isMedicalDataEnabled()) {
    return <Navigate to={redirectTo} replace />;
  }

  return children;
};

export const FeatureTagRoute = ({ children, tag, redirectTo = '/', fallback = null }) => {
  const { isAuthenticated } = useAuthSync();
  const { isEnabled, isLoading } = useOrganizationFeatureTag(tag);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          backgroundColor: 'var(--color-neutral-50)',
          padding: '2rem'
        }}
      >
        <div
          className="loading-spinner"
          style={{
            width: '50px',
            height: '50px',
            border: '4px solid var(--color-neutral-200)',
            borderTop: '4px solid var(--color-primary-500)',
            marginBottom: 0
          }}
        ></div>
      </div>
    );
  }

  if (isEnabled) {
    return children;
  }

  if (fallback) {
    return fallback;
  }

  return <Navigate to={redirectTo} replace />;
};

export default ProtectedRoute;